import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import lodashCompact from 'lodash/fp/compact';

import {
  USER_ROLE_RENTER,
  USER_ROLE_ANONYMOUS,
} from 'common/userRoles';

import {
  TOUR_STATUS_REQUESTED,
  TOUR_STATUS_SCHEDULED,
  TOUR_STATUS_COMPLETED,
  TOUR_STATUS_CANCELLED,
} from 'common/tourConstants';

import {
  LISTING_DATA_SOURCE_REALSAVVY,
} from 'common/listingConstants';

import {
  getUpcomingTours,
  getOrderedToursWithStatuses,
} from 'common/tourHelpers';

import TabBar from 'client/components/TabBar';
import Navbar from 'client/components/Navbar';
import NoTours from 'client/components/NoTours';
import SrTourList from 'client/components/SrTourList';
import SrContinueApplicationButton from 'client/components/SrContinueApplicationButton';

import branch from 'client/core/branch';

import {
  SCREEN_ID_TOURS,
} from 'client/constants/screenIds';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';

import ListingActions from 'client/actions/Listing';
import TourActions from 'client/actions/Tour';
import UserActions from 'client/actions/User';

const TOUR_SECTION_HEADLINE_UPCOMING = 'Upcoming Tours';
const TOUR_SECTION_HEADLINE_COMPLETED = 'Completed Tours';
const TOUR_SECTION_HEADLINE_CANCELLED = 'Archived Tours';

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_RENTER,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_TOURS)
@withScrollToTop
@branch({
  tours: ['tours'],
  recommendedListings: ['recommendedListings'],
  listingPhotoResolution: ['listingPhotoResolution'],
  userAgent: ['analytics', 'userAgent'],
  currentUser: ['currentUser'],
})
class ToursPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Apply Now | Sunroom Rentals',
    section: 'Tours',
    // canonical: '/',
    keywords: 'rent, home, austin, tour, apartment',
    description: (
      'Spend less time scheduling tours, our rental experts will schedule ' +
      'and confirm tours for you.'
    ),
    image: 'https://sunroomrentals.com/images/metadata/og-house.jpg',
  })

  static fetchData(match, inject) {
    const tours = inject.state.get(['tours']);
    const currentUserId = inject.state.get(['currentUser', 'id']);
    const requestedTours = getUpcomingTours(tours);
    if (requestedTours && requestedTours.length >= 1) {
      const viewedListingIds = [];
      requestedTours[0].listings.forEach((listing) => {
        viewedListingIds.push(listing.id);
      });
      const firstListingInTour = requestedTours[0].listings[0];
      return Promise.all([
        ListingActions.getRecommendedList(inject, {
          count: 3,
          filters: {
            addressZip: firstListingInTour.addressZip,
            viewedListingIds: viewedListingIds,
            dataSource: [
              LISTING_DATA_SOURCE_REALSAVVY,
            ],
          },
          reloadRecommendations: true,
        }),
        UserActions.getCustomerSupportPhoneNumber(inject, { userId: currentUserId }),
      ]);
    }
    return UserActions.getCustomerSupportPhoneNumber(inject, { userId: currentUserId });
  }

  static propTypes = {
    recommendedListings: PropTypes.object,
    tours: PropTypes.array,
    listingPhotoResolution: PropTypes.number.isRequired,
    userAgent: PropTypes.object.isRequired,
    location: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);

    const locationQuery = queryString.parse(props.location.search);

    this.state = {
      highlightListingId: locationQuery.listingId
        ? parseInt(locationQuery.listingId, 10)
        : null,
    };

    this.lastUpdate = Date.now();
  }

  componentDidMount() {
    this._loadUserTours();
    this._updateInterval = global.setInterval(() => {
      this._loadUserTours();
    }, 5000);
  }

  componentWillUnmount() {
    if (this._updateInterval) {
      global.clearInterval(this._updateInterval);
      this._updateInterval = null;
    }
  }

  _loadUserTours = () => {
    this.props.dispatch(TourActions.getList, {
      reloadTours: true,
    });
  }

  render() {
    const {
      tours,
      userAgent,
      listingPhotoResolution,
      dispatch,
      recommendedListings : {
        records: recommenedListingsRecords,
      },
      currentUser,
    } = this.props;

    const {
      highlightListingId,
    } = this.state;

    const tourSections = lodashCompact([
      {
        headline: TOUR_SECTION_HEADLINE_UPCOMING,
        isUpcoming: true,
        isCompleted: false,
        isArchived: false,
        tours: getOrderedToursWithStatuses(tours, [
          TOUR_STATUS_REQUESTED,
          TOUR_STATUS_SCHEDULED,
        ]),
        recommendedListings: recommenedListingsRecords,
      },
      {
        headline: TOUR_SECTION_HEADLINE_COMPLETED,
        isUpcoming: false,
        isCompleted: true,
        isArchived: false,
        tours: getOrderedToursWithStatuses(tours, [
          TOUR_STATUS_COMPLETED,
        ]),
      },
      // TODO: Show "cancelled" tours somewhere - cc @dsalehipour
      {
        headline: TOUR_SECTION_HEADLINE_CANCELLED,
        isUpcoming: false,
        isCompleted: false,
        isArchived: true,
        tours: getOrderedToursWithStatuses(tours, [
          TOUR_STATUS_CANCELLED,
        ]),
      },
    ]).filter(({ tours }) => tours.length > 0);

    return (
      <div className='page-tours'>
        <div>
          <Navbar />
          {!currentUser.roles.includes(USER_ROLE_ANONYMOUS) && (
            <SrContinueApplicationButton />
          )}
          <TabBar />
          {tours && !tours.length
            ? <NoTours />
            : (
              <SrTourList
                tourSections={tourSections}
                highlightListingId={highlightListingId}
                dispatch={dispatch}
                listingPhotoResolution={listingPhotoResolution}
                userAgent={userAgent}
                currentUser={currentUser}
              />
            )
          }
        </div>

      </div>
    );
  }
}

ToursPage.displayName = 'ToursPage';

export default ToursPage;
